/**
 * Intake Package — the tenant-aware required-documents checklist rendered on
 * the Account/Group Documents tab. Slot contents mirror the cbs-portal demo
 * (groupCompliance.ts): the base set is the same for every tenant; Synrgy adds
 * the Clear Spring form (see `resolveClearSpringFormEnabled` in menuFlags).
 *
 * Status is DERIVED, never stored:
 *  - `rfc` / `ach` slots read the linked RfcSubmission's form JSON;
 *  - every other slot looks for a document with its typed category, falling
 *    back to a filename regex so legacy GENERAL uploads still register;
 *  - an ACTIVE (live) account shows 'received' instead of 'missing' — it
 *    passed go-live, the file just isn't a discrete upload here.
 */
import type {RfcAchForm} from './lib/rfcSchema';

export type IntakeSlotKey =
  | 'rfc'
  | 'msa'
  | 'incomeWorksheet'
  | 'w9'
  | 'ach'
  | 'erisaDoc'
  | 'section125'
  | 'clearspring';

export type IntakeSlotState = 'onfile' | 'received' | 'missing';

export interface IntakePackageSlot {
  key: IntakeSlotKey;
  label: string;
  help: string;
  /** AccountsDocumentCategory value that satisfies this slot (string literal —
   * @common cannot import the Prisma client). Absent for RFC-derived slots. */
  category?: string;
  /** Filename fallback so legacy GENERAL uploads still count. */
  matcher?: RegExp;
}

const BASE_SLOTS: IntakePackageSlot[] = [
  {key: 'rfc', label: 'RFC', help: 'Digital request for coverage.'},
  {
    key: 'msa',
    label: 'MSA',
    help: 'Master Service Agreement (signed).',
    category: 'MSA',
    matcher: /\bmsa\b/i,
  },
  {
    key: 'incomeWorksheet',
    label: 'Income worksheet',
    help: 'Drives pay frequency + deduction structure.',
    category: 'INCOME_WORKSHEET',
    matcher: /income|worksheet/i,
  },
  {
    key: 'w9',
    label: 'W-9',
    help: 'Group / agent W-9 on file.',
    category: 'W9',
    // Word boundary so e.g. "review-9…" doesn't register as a W-9.
    matcher: /\bw-?9\b/i,
  },
  {
    key: 'ach',
    label: 'ACH info / bank letter',
    help: 'Banking on file — bank letter or voided check.',
    category: 'ACH_BANK_LETTER',
    matcher: /bank\s*letter|voided\s*check|\bach\b/i,
  },
  {
    key: 'erisaDoc',
    label: 'ERISA / plan document',
    help: 'Plan document / SPD for the ERISA plan.',
    category: 'ERISA_PLAN_DOC',
    matcher: /erisa|spd|plan doc/i,
  },
  {
    key: 'section125',
    label: '§125 plan document',
    help: 'Required when any product is pre-tax.',
    category: 'SECTION_125_DOC',
    matcher: /125|cafeteria|\bpop\b/i,
  },
];

const CLEAR_SPRING_SLOT: IntakePackageSlot = {
  key: 'clearspring',
  label: 'Clear Spring form',
  help: 'Synrgy-specific Clear Spring form.',
  category: 'CLEAR_SPRING_FORM',
  matcher: /clear\s*spring/i,
};

export function intakePackageSlots(clearSpringEnabled: boolean): IntakePackageSlot[] {
  return clearSpringEnabled ? [...BASE_SLOTS, CLEAR_SPRING_SLOT] : [...BASE_SLOTS];
}

/**
 * Whether an RFC ACH form block contains any captured banking data. Ported
 * from the RfcTab `hasAchData` — the RFC tab and this checklist must agree on
 * what "banking on file" means.
 */
export function achFormHasData(achForm: RfcAchForm | null | undefined): boolean {
  if (!achForm) return false;
  const {bankAccount, achDetails, authorization} = achForm;
  const addr = bankAccount?.address;
  const bankAddr = achDetails?.bankAddress;
  return !!(
    bankAccount?.businessNameOnAccount?.trim() ||
    bankAccount?.accountHolderFirstName?.trim() ||
    bankAccount?.accountHolderLastName?.trim() ||
    bankAccount?.contactPhone?.trim() ||
    bankAccount?.contactEmail?.trim() ||
    addr?.street?.trim() ||
    addr?.city?.trim() ||
    addr?.state?.trim() ||
    addr?.zip?.trim() ||
    achDetails?.bankName?.trim() ||
    achDetails?.routingNumber?.trim() ||
    achDetails?.accountNumber?.trim() ||
    bankAddr?.street?.trim() ||
    bankAddr?.city?.trim() ||
    bankAddr?.state?.trim() ||
    bankAddr?.zip?.trim() ||
    authorization?.authorizedSignature?.trim() ||
    authorization?.signatureDate?.trim()
  );
}

/** ACH lives per-location at groupInfo.locations[].achForm inside the 125/MEC
 * form JSON (see rfcSchema.ts). Structural access — form data arrives as
 * loosely-typed JSON from the API. */
export function rfcFormDataHasAch(formData: unknown): boolean {
  const locations = (
    formData as {groupInfo?: {locations?: Array<{achForm?: RfcAchForm}>}} | null | undefined
  )?.groupInfo?.locations;
  return (locations ?? []).some((location) => achFormHasData(location?.achForm));
}

export interface IntakeSlotStatus {
  key: IntakeSlotKey;
  label: string;
  help: string;
  state: IntakeSlotState;
  /** Where the evidence lives — drives the "View" jump link. */
  source: 'rfc' | 'documents' | null;
}

export function deriveIntakePackage(input: {
  slots: IntakePackageSlot[];
  hasRfcFormData: boolean;
  hasAchFormData: boolean;
  documents: ReadonlyArray<{category: string; fileName: string}>;
  isLive: boolean;
}): IntakeSlotStatus[] {
  const {slots, hasRfcFormData, hasAchFormData, documents, isLive} = input;

  const fallback = (): {state: IntakeSlotState; source: null} => ({
    state: isLive ? 'received' : 'missing',
    source: null,
  });

  return slots.map((slot) => {
    let resolved: {state: IntakeSlotState; source: 'rfc' | 'documents' | null};
    if (slot.key === 'rfc' && hasRfcFormData) {
      resolved = {state: 'onfile', source: 'rfc'};
    } else if (slot.key === 'rfc' && documents.some((d) => d.category === 'RFC_ATTACHMENT')) {
      resolved = {state: 'onfile', source: 'documents'};
    } else if (slot.key === 'ach' && hasAchFormData) {
      resolved = {state: 'onfile', source: 'rfc'};
    } else if (
      (slot.category && documents.some((d) => d.category === slot.category)) ||
      (slot.matcher && documents.some((d) => slot.matcher!.test(d.fileName)))
    ) {
      resolved = {state: 'onfile', source: 'documents'};
    } else {
      resolved = fallback();
    }
    return {key: slot.key, label: slot.label, help: slot.help, ...resolved};
  });
}
